import { decodeHeic, isHeic } from "@su-engineering/heic";

const HEIC_EXTENSIONS = new Set(["heic", "heif"]);
const HEIC_MIME_TYPES = new Set(["image/heic", "image/heif", "image/heic-sequence", "image/heif-sequence"]);

export function hasHeicHint(file: Blob & { name?: string }): boolean {
  const extension = file.name?.split(".").pop()?.toLowerCase() ?? "";
  return HEIC_EXTENSIONS.has(extension) || HEIC_MIME_TYPES.has(file.type.toLowerCase());
}

async function decodeHeicBitmap(file: Blob): Promise<ImageBitmap> {
  const decoded = await decodeHeic(file, {
    // Keep the LGPL codec in a separate lazy chunk. Most supported platforms
    // use native/WebCodecs; the fallback is fetched only when those fail.
    wasmLoader: async () => (await import("@su-engineering/heic/wasm")).wasmDecoder,
  });
  return decoded.image;
}

/**
 * Decode a browser image, adding HEIC/HEIF support without changing the source.
 * Container rotation/mirroring is applied in memory by the HEIC decoder.
 */
export async function decodeImageBitmap(file: File): Promise<ImageBitmap> {
  if (hasHeicHint(file)) return decodeHeicBitmap(file);

  try {
    return await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch (nativeError) {
    // A copied iPhone photo can have a wrong extension or MIME type. Inspect its
    // container only after normal browser decoding fails, avoiding extra reads
    // for the common JPEG/PNG path.
    try {
      if ((await isHeic(file)).isHeic) return await decodeHeicBitmap(file);
    } catch {
      // Preserve the browser's original error for genuinely unsupported files.
    }
    throw nativeError;
  }
}

export function explainDecodeFailure(file: File, error: unknown): string {
  const reason = error instanceof Error && error.message ? ` (${error.message})` : "";
  if (hasHeicHint(file)) {
    return `PhotoTidy could not decode this HEIC/HEIF file${reason}. It was not analyzed; this does not mean the photo is damaged.`;
  }
  return `PhotoTidy could not decode this image${reason}. It was not analyzed; this does not mean the file is damaged.`;
}
